import type {
  ManagerialDashboard,
  ManagerialDashboardActiveCampaign,
  ManagerialDashboardCampaignOption,
  ManagerialDashboardCampaignPerformance,
  ManagerialDashboardCampaignPerformanceStatus,
  ManagerialDashboardChartPeriod,
  ManagerialDashboardClientProfitability,
  ManagerialDashboardFilterOption,
  ManagerialDashboardKpi,
  ManagerialDashboardOrderStatus,
  ManagerialDashboardOrderStatusCounts,
  ManagerialDashboardPeriod,
  ManagerialDashboardRecentOrder,
  ManagerialDashboardSalesEvolution,
  ManagerialDashboardSalesEvolutionPoint,
  ManagerialDashboardSection,
  ManagerialDashboardSectionResponse,
} from '@/types/api'
import { isExecutiveFinance } from './executive-finance'
import { isMeasurements } from './measurements'

const PERIODS: ManagerialDashboardPeriod[] = ['12m', '30d', '7d', 'month']
const STATUSES: ManagerialDashboardOrderStatus[] = [
  'all',
  'paid',
  'pending',
  'canceled',
  'expired',
]
const CHART_PERIODS: ManagerialDashboardChartPeriod[] = [
  '7d',
  '30d',
  'month',
  'all',
  'custom',
]
const RECENT_ORDER_STATUSES: ManagerialDashboardRecentOrder['status'][] = [
  'paid',
  'pending',
  'canceled',
]
const CAMPAIGN_PERFORMANCE_STATUSES: ManagerialDashboardCampaignPerformanceStatus[] = [
  'draft',
  'active',
  'finished',
  'cancelled',
]
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const ISO_DATETIME_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isPeriod(value: unknown): value is ManagerialDashboardPeriod {
  return typeof value === 'string' && PERIODS.includes(value as ManagerialDashboardPeriod)
}

function isStatus(value: unknown): value is ManagerialDashboardOrderStatus {
  return (
    typeof value === 'string' &&
    STATUSES.includes(value as ManagerialDashboardOrderStatus)
  )
}

function isChartPeriod(value: unknown): value is ManagerialDashboardChartPeriod {
  return (
    typeof value === 'string' &&
    CHART_PERIODS.includes(value as ManagerialDashboardChartPeriod)
  )
}

function isNullableId(value: unknown): value is number | null {
  return (
    value === null || (typeof value === 'number' && Number.isInteger(value) && value > 0)
  )
}

function isFilterOption(value: unknown): value is ManagerialDashboardFilterOption {
  if (!isRecord(value)) return false
  return (
    typeof value.id === 'number' &&
    Number.isInteger(value.id) &&
    typeof value.label === 'string'
  )
}

function isCampaignOption(value: unknown): value is ManagerialDashboardCampaignOption {
  if (!isRecord(value)) return false
  if (typeof value.id !== 'number' || !Number.isInteger(value.id)) return false
  if (typeof value.label !== 'string') return false
  return value.institution_id === null || typeof value.institution_id === 'number'
}

function isNonNegativeInt(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
}

function isOrderStatusCounts(
  value: unknown
): value is ManagerialDashboardOrderStatusCounts {
  if (!isRecord(value)) return false
  return (
    isNonNegativeInt(value.paid) &&
    isNonNegativeInt(value.pending) &&
    isNonNegativeInt(value.canceled)
  )
}

function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && ISO_DATE_PATTERN.test(value)
}

function isNonNegativeFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

function isSalesEvolutionPoint(
  value: unknown
): value is ManagerialDashboardSalesEvolutionPoint {
  if (!isRecord(value)) return false
  return (
    isIsoDate(value.date) &&
    isNonNegativeInt(value.sold_numbers) &&
    isNonNegativeFiniteNumber(value.revenue)
  )
}

function isSalesEvolution(value: unknown): value is ManagerialDashboardSalesEvolution {
  if (!isRecord(value)) return false
  if (
    typeof value.period !== 'string' ||
    !CHART_PERIODS.includes(value.period as ManagerialDashboardChartPeriod)
  ) {
    return false
  }
  if (!isIsoDate(value.date_from) || !isIsoDate(value.date_to)) return false
  if (!Array.isArray(value.points)) return false
  return value.points.every(isSalesEvolutionPoint)
}

function isHttpUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value === '') return false
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

function isActiveCampaign(value: unknown): value is ManagerialDashboardActiveCampaign {
  if (!isRecord(value)) return false
  if (typeof value.id !== 'number' || !Number.isInteger(value.id) || value.id <= 0)
    return false
  if (typeof value.title !== 'string' || value.title === '') return false
  if (value.image_url !== null && !isHttpUrl(value.image_url)) return false
  if (typeof value.prize_label !== 'string') return false
  if (value.draw_date !== null && !isIsoDate(value.draw_date)) return false
  if (value.status !== 'active') return false
  return isNonNegativeInt(value.sold_numbers) && isNonNegativeFiniteNumber(value.revenue)
}

function isActiveCampaignList(
  value: unknown
): value is ManagerialDashboardActiveCampaign[] {
  return Array.isArray(value) && value.every(isActiveCampaign)
}

function isIsoDateTime(value: unknown): value is string {
  return typeof value === 'string' && ISO_DATETIME_PATTERN.test(value)
}

function isRecentOrderStatus(
  value: unknown
): value is ManagerialDashboardRecentOrder['status'] {
  return (
    typeof value === 'string' &&
    RECENT_ORDER_STATUSES.includes(value as ManagerialDashboardRecentOrder['status'])
  )
}

function isRecentOrder(value: unknown): value is ManagerialDashboardRecentOrder {
  if (!isRecord(value)) return false
  if (typeof value.id !== 'number' || !Number.isInteger(value.id) || value.id <= 0)
    return false
  if (!isIsoDateTime(value.occurred_at)) return false
  if (typeof value.customer_name !== 'string' || value.customer_name === '') return false
  if (!isNonNegativeFiniteNumber(value.amount)) return false
  return isRecentOrderStatus(value.status)
}

function isRecentOrderList(value: unknown): value is ManagerialDashboardRecentOrder[] {
  return Array.isArray(value) && value.every(isRecentOrder)
}

function isClientProfitabilityRow(
  value: unknown
): value is ManagerialDashboardClientProfitability {
  if (!isRecord(value)) return false
  if (typeof value.id !== 'number' || !Number.isInteger(value.id) || value.id <= 0)
    return false
  if (typeof value.name !== 'string' || value.name.trim() === '') return false
  return (
    isNonNegativeFiniteNumber(value.revenue) &&
    isNonNegativeFiniteNumber(value.costs) &&
    isNonNegativeFiniteNumber(value.profit)
  )
}

function isClientProfitabilityList(
  value: unknown
): value is ManagerialDashboardClientProfitability[] {
  return Array.isArray(value) && value.every(isClientProfitabilityRow)
}

function isCampaignPerformanceStatus(
  value: unknown
): value is ManagerialDashboardCampaignPerformanceStatus {
  return (
    typeof value === 'string' &&
    CAMPAIGN_PERFORMANCE_STATUSES.includes(
      value as ManagerialDashboardCampaignPerformanceStatus
    )
  )
}

function isCampaignPerformanceRow(
  value: unknown
): value is ManagerialDashboardCampaignPerformance {
  if (!isRecord(value)) return false
  if (typeof value.id !== 'number' || !Number.isInteger(value.id) || value.id <= 0)
    return false
  if (typeof value.campaign_name !== 'string' || value.campaign_name.trim() === '')
    return false
  if (typeof value.client_name !== 'string' || value.client_name.trim() === '')
    return false
  if (!isCampaignPerformanceStatus(value.status)) return false
  return (
    isNonNegativeFiniteNumber(value.revenue) &&
    isNonNegativeFiniteNumber(value.costs) &&
    isNonNegativeFiniteNumber(value.profit)
  )
}

function isCampaignPerformanceList(
  value: unknown
): value is ManagerialDashboardCampaignPerformance[] {
  return Array.isArray(value) && value.every(isCampaignPerformanceRow)
}

function isKpi(value: unknown): value is ManagerialDashboardKpi {
  if (!isRecord(value)) return false
  return (
    typeof value.id === 'string' &&
    value.id !== '' &&
    typeof value.label === 'string' &&
    value.label !== '' &&
    typeof value.formatted_value === 'string' &&
    value.formatted_value !== ''
  )
}

function isRevenueOrigin(value: unknown): boolean {
  return (
    isRecord(value) &&
    ['site', 'referral', 'affiliates', 'paid-traffic', 'unknown'].every((key) =>
      isNonNegativeFiniteNumber(value[key])
    )
  )
}

export function isManagerialDashboard(value: unknown): value is ManagerialDashboard {
  if (!isRecord(value)) return false
  if (value.measurements !== undefined && !isMeasurements(value.measurements))
    return false
  if (
    value.executive_finance !== undefined &&
    !isExecutiveFinance(value.executive_finance)
  )
    return false
  if (typeof value.generated_at !== 'string' || value.generated_at === '') return false
  if (!isRecord(value.applied_filters)) return false
  if (!isPeriod(value.applied_filters.period)) return false
  if (!isStatus(value.applied_filters.status)) return false
  if (!isChartPeriod(value.applied_filters.chart_period)) return false
  if (!isIsoDate(value.applied_filters.chart_from)) return false
  if (!isIsoDate(value.applied_filters.chart_to)) return false
  if (!isNullableId(value.applied_filters.institution_id)) return false
  if (!isNullableId(value.applied_filters.campaign_id)) return false
  if (!isRecord(value.filter_options)) return false
  if (!Array.isArray(value.filter_options.institutions)) return false
  if (!Array.isArray(value.filter_options.campaigns)) return false
  if (!value.filter_options.institutions.every(isFilterOption)) return false
  if (!value.filter_options.campaigns.every(isCampaignOption)) return false
  if (!isRecord(value.kpis)) return false
  return (
    isKpi(value.kpis.leads) &&
    isKpi(value.kpis.conversion_rate) &&
    isRevenueOrigin(value.revenue_origin) &&
    isKpi(value.kpis.platform_profit) &&
    isKpi(value.kpis.platform_margin) &&
    isKpi(value.kpis.sold_numbers) &&
    isKpi(value.kpis.active_institutions) &&
    isKpi(value.kpis.paid_orders) &&
    isKpi(value.kpis.average_ticket) &&
    isKpi(value.kpis.active_clients) &&
    isKpi(value.kpis.active_campaigns) &&
    isKpi(value.kpis.active_affiliates) &&
    isKpi(value.kpis.revenue) &&
    isOrderStatusCounts(value.order_status) &&
    isActiveCampaignList(value.active_campaigns) &&
    isRecentOrderList(value.recent_orders) &&
    isSalesEvolution(value.sales_evolution) &&
    isClientProfitabilityList(value.client_profitability) &&
    isCampaignPerformanceList(value.campaign_performance)
  )
}

export function resolveManagerialDashboard(value: unknown): ManagerialDashboard | null {
  if (isManagerialDashboard(value)) return value
  if (!isRecord(value) || !('data' in value)) return null
  return isManagerialDashboard(value.data) ? value.data : null
}

/** Validate only the requested block; missing blocks are never fabricated as zero. */
export function resolveManagerialDashboardSection(
  value: unknown,
  section: ManagerialDashboardSection
): ManagerialDashboardSectionResponse | null {
  const row = isRecord(value) && isRecord(value.data) ? value.data : value
  if (!isRecord(row) || row.section !== section || !isIsoDateTime(row.generated_at))
    return null
  let valid = false
  switch (section) {
    case 'overview':
      valid =
        isRecord(row.kpis) &&
        [
          'leads',
          'conversion_rate',
          'platform_profit',
          'platform_margin',
          'sold_numbers',
          'active_institutions',
          'paid_orders',
          'average_ticket',
          'active_clients',
          'active_campaigns',
          'active_affiliates',
          'revenue',
        ].every((key) => isKpi((row.kpis as Record<string, unknown>)[key])) &&
        isOrderStatusCounts(row.order_status) &&
        isRecord(row.filter_options) &&
        Array.isArray(row.filter_options.institutions) &&
        row.filter_options.institutions.every(isFilterOption) &&
        Array.isArray(row.filter_options.campaigns) &&
        row.filter_options.campaigns.every(isCampaignOption)
      break
    case 'sales_evolution':
      valid = isSalesEvolution(row.sales_evolution)
      break
    case 'activity':
      valid =
        isActiveCampaignList(row.active_campaigns) && isRecentOrderList(row.recent_orders)
      break
    case 'revenue_origin':
      valid = isRevenueOrigin(row.revenue_origin)
      break
    case 'client_profitability':
      valid = isClientProfitabilityList(row.client_profitability)
      break
    case 'campaign_performance':
      valid = isCampaignPerformanceList(row.campaign_performance)
      break
    case 'finance':
      valid =
        isExecutiveFinance(row.executive_finance) &&
        isMeasurements(row.measurements) &&
        row.measurements.sections.length === 1 &&
        row.measurements.sections[0].id === 'efficiency'
      break
    default:
      valid =
        isMeasurements(row.measurements) &&
        row.measurements.sections.length === 1 &&
        row.measurements.sections[0].id === section
  }
  if (!valid) return null
  const fields =
    section === 'overview'
      ? ['kpis', 'filter_options', 'order_status']
      : section === 'activity'
        ? ['active_campaigns', 'recent_orders']
        : section === 'finance'
          ? ['executive_finance', 'measurements']
          : ['relationship', 'buyers', 'products', 'funnel'].includes(section)
            ? ['measurements']
            : [section]
  return Object.fromEntries([
    ['section', section],
    ['generated_at', row.generated_at],
    ...fields.map((field) => [field, row[field]]),
  ]) as ManagerialDashboardSectionResponse
}

export const MANAGERIAL_DASHBOARD_LOAD_ERROR =
  'Não foi possível carregar o painel gerencial.'

export const MANAGERIAL_DASHBOARD_NETWORK_ERROR =
  'Não foi possível concluir. Tente de novo em instantes.'

function looksTechnical(message: string): boolean {
  return /unauthenticated|unauthorized|forbidden|validation failed|given data was invalid|server error|http \d|stack/i.test(
    message
  )
}

function firstValidationMessage(error: object): string {
  if (!('errors' in error) || error.errors === null || typeof error.errors !== 'object') {
    return ''
  }

  for (const value of Object.values(error.errors as Record<string, unknown>)) {
    if (Array.isArray(value)) {
      const first = value.find((item) => typeof item === 'string' && item !== '')
      if (typeof first === 'string') {
        return first
      }
    }
    if (typeof value === 'string' && value !== '') {
      return value
    }
  }

  return ''
}

export function resolveManagerialDashboardError(error: unknown): string {
  if (error instanceof Error && /abort/i.test(error.name)) {
    return MANAGERIAL_DASHBOARD_LOAD_ERROR
  }

  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = error.status
    const message =
      'message' in error && typeof error.message === 'string' ? error.message : ''

    if (typeof status === 'number' && status === 0) {
      return MANAGERIAL_DASHBOARD_NETWORK_ERROR
    }

    if (typeof status === 'number' && status === 422) {
      const fromErrors = firstValidationMessage(error)
      if (fromErrors !== '' && !looksTechnical(fromErrors)) {
        return fromErrors
      }
      if (message !== '' && !looksTechnical(message)) {
        return message
      }
      return 'Não foi possível aplicar o período do gráfico. Confira as datas.'
    }

    if (
      typeof status === 'number' &&
      status === 403 &&
      message !== '' &&
      !looksTechnical(message)
    ) {
      return message
    }

    if (message !== '' && !looksTechnical(message)) {
      return message
    }
  }

  return MANAGERIAL_DASHBOARD_LOAD_ERROR
}
